<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="680px">
    <v-card class="profile-dialog-card curriculum-dialog">
      <div class="profile-dialog-header curriculum-dialog__header">
        <div class="curriculum-dialog__title">
          <v-avatar size="44" color="green lighten-5">
            <v-icon color="primary">mdi-book-open-page-variant-outline</v-icon>
          </v-avatar>
          <div>
            <span>{{ sourceLabel }}</span>
            <h2>Manage curriculums</h2>
            <p>Choose one or more curriculums used for grading and report cards.</p>
          </div>
        </div>
        <v-btn icon aria-label="Close curriculum dialog" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text v-if="selectedClassRoom" class="profile-dialog-body curriculum-dialog__body">
        <section class="curriculum-dialog__classroom">
          <div class="curriculum-dialog__classroom-icon">
            <v-icon color="primary">mdi-door-open</v-icon>
          </div>
          <div>
            <strong>{{ selectedClassRoom.name || "Unnamed classroom" }}</strong>
            <span>{{ departmentName }}</span>
          </div>
          <v-chip small outlined color="primary">{{ assignedCurriculums.length }} assigned</v-chip>
        </section>

        <section class="curriculum-dialog__section">
          <div class="curriculum-dialog__section-heading">
            <div>
              <strong>Assigned curriculums</strong>
              <span>These curriculums are currently connected to this classroom.</span>
            </div>
          </div>
          <div v-if="assignedCurriculums.length" class="curriculum-dialog__chips">
            <v-chip
              v-for="curriculum in assignedCurriculums"
              :key="curriculum.id"
              outlined
              color="primary"
              close
              @click:close="removeAssigned(curriculum)"
            >
              {{ curriculum.name }}
            </v-chip>
          </div>
          <div v-else class="curriculum-dialog__empty">
            <v-icon size="34" color="grey lighten-1">mdi-book-open-blank-variant-outline</v-icon>
            <strong>No curriculums assigned</strong>
            <span>Select curriculums below and save your changes.</span>
          </div>
        </section>

        <section class="curriculum-dialog__section">
          <div class="curriculum-dialog__section-heading">
            <div>
              <strong>Select curriculums</strong>
              <span>You can attach more than one curriculum.</span>
            </div>
          </div>
          <v-autocomplete
            v-model="selectedCurriculumIds"
            :items="curriculums"
            :item-disabled="isCurriculumEmpty"
            item-text="name"
            item-value="id"
            multiple
            chips
            small-chips
            clearable
            outlined
            dense
            label="Select curriculums"
            persistent-hint
            hint="Curriculums without subjects cannot be assigned."
            :menu-props="{ offsetY: true, maxHeight: 300 }"
          >
            <template v-slot:item="{ item }">
              <span>{{ item.name }}</span>
              <v-spacer />
              <v-chip x-small label :color="subjectCount(item) ? 'green' : 'grey'" text-color="white">
                {{ subjectCount(item) }} subjects
              </v-chip>
              <v-chip v-if="!subjectCount(item)" x-small label class="ml-2" color="red" text-color="white">
                Empty
              </v-chip>
            </template>
            <template v-slot:selection="{ item }">
              <v-chip small class="mr-1" label>{{ item.name }} ({{ subjectCount(item) }})</v-chip>
            </template>
          </v-autocomplete>
        </section>
      </v-card-text>

      <v-divider />
      <v-card-actions class="profile-dialog-actions curriculum-dialog__actions">
        <span>{{ selectedCurriculumIds.length }} selected</span>
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="loading" @click="submit">Save changes</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import curriculumService from "@/modules/ReportCard/Curriculums/service.js";
import classRoomService from "../service.js";
import virtualClassRoomService from "@/modules/VirtualClassRoom/service.js";

export default {
  name: "Assign-Curriculum-To-Classroom",
  props: {
    editDialog: { default: false },
    selectedClassRoom: { type: Object },
    source: { type: String, default: "regular" },
  },
  data: () => ({
    dialog: false,
    loading: false,
    curriculums: [],
    assignedCurriculums: [],
    selectedCurriculumIds: [],
  }),
  computed: {
    sourceLabel() { return this.source === "virtual" ? "Virtual classroom" : "Classroom"; },
    departmentName() {
      return this.selectedClassRoom && this.selectedClassRoom.Class
        ? this.selectedClassRoom.Class.name
        : "No department";
    },
  },
  watch: {
    editDialog(value) { this.dialog = value; },
    async dialog(value) {
      if (!value) return;
      await this.getCurriculums();
      const assigned = Array.isArray(this.selectedClassRoom && this.selectedClassRoom.Curriculums)
        ? this.selectedClassRoom.Curriculums
        : [];
      this.assignedCurriculums = assigned;
      this.selectedCurriculumIds = assigned.map((curriculum) => curriculum.id);
    },
    selectedCurriculumIds(value) {
      if (!Array.isArray(value)) return;
      const validIds = value.filter((id) => {
        const curriculum = this.curriculums.find((item) => item.id === id);
        return curriculum && this.subjectCount(curriculum) > 0;
      });
      if (validIds.length !== value.length) {
        this.selectedCurriculumIds = validIds;
        this.showToast("error", "You can’t assign a curriculum with 0 subjects.");
        return;
      }
      this.assignedCurriculums = this.curriculums.filter((item) => validIds.includes(item.id));
    },
  },
  methods: {
    isCurriculumEmpty(item) { return this.subjectCount(item) === 0; },
    subjectCount(curriculum) {
      if (!curriculum) return 0;
      const meta = curriculum.subjectMeta;
      if (meta && Array.isArray(meta.subjects)) return meta.subjects.length;
      if (Array.isArray(meta)) return meta.length;
      if (meta && typeof meta === "object") {
        if (meta.subjects && typeof meta.subjects === "object") return Object.keys(meta.subjects).length;
        return Object.keys(meta).length;
      }
      return 0;
    },
    removeAssigned(curriculum) {
      this.selectedCurriculumIds = this.selectedCurriculumIds.filter((id) => id !== curriculum.id);
    },
    async getCurriculums() {
      const response = await curriculumService.getAll();
      this.curriculums = response.data || [];
    },
    async submit() {
      this.loading = true;
      try {
        const service = this.source === "virtual" ? virtualClassRoomService : classRoomService;
        await service.setCurriculums(this.selectedClassRoom.id, {
          CurriculumIds: this.selectedCurriculumIds,
        });
        this.showToast("success", "Curriculums updated");
        this.$emit("submit");
      } catch (error) {
        this.showToast("error", error && error.data && error.data.message
          ? error.data.message
          : "Unable to update curriculums");
      } finally {
        this.loading = false;
      }
    },
    showToast(icon, title) {
      this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
        .fire({ icon, title });
    },
    close() {
      this.dialog = false;
      this.selectedCurriculumIds = [];
      this.assignedCurriculums = [];
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.curriculum-dialog { overflow: hidden; border-radius: 14px !important; }
.curriculum-dialog__header { background: #fbfcfb; }
.curriculum-dialog__title { display: flex; align-items: center; gap: 12px; }
.curriculum-dialog__title > div:last-child { display: flex; flex-direction: column; }
.curriculum-dialog__title span { color: #43a047; font-size: 10px; font-weight: 750; letter-spacing: .08em; text-transform: uppercase; }
.curriculum-dialog__title h2 { margin: 2px 0 0; color: #172033; font-size: 21px; line-height: 1.2; }
.curriculum-dialog__title p { margin: 3px 0 0; color: #7b8799; font-size: 11px; }
.curriculum-dialog__body { min-height: 410px; padding: 22px 24px !important; }
.curriculum-dialog__classroom { display: flex; align-items: center; gap: 11px; padding: 13px; border: 1px solid #e3e9e3; border-radius: 11px; background: #f8faf8; }
.curriculum-dialog__classroom-icon { display: flex; width: 38px; height: 38px; align-items: center; justify-content: center; border-radius: 9px; background: #eaf6eb; }
.curriculum-dialog__classroom > div:nth-child(2) { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.curriculum-dialog__classroom strong { color: #253047; font-size: 13px; }
.curriculum-dialog__classroom span { margin-top: 2px; color: #879288; font-size: 10px; }
.curriculum-dialog__section { margin-top: 22px; }
.curriculum-dialog__section-heading { margin-bottom: 10px; }
.curriculum-dialog__section-heading > div { display: flex; flex-direction: column; }
.curriculum-dialog__section-heading strong { color: #344238; font-size: 12px; }
.curriculum-dialog__section-heading span { margin-top: 2px; color: #8b968e; font-size: 10px; }
.curriculum-dialog__chips { display: flex; flex-wrap: wrap; gap: 7px; padding: 12px; border: 1px solid #e5eae5; border-radius: 10px; background: #fbfcfb; }
.curriculum-dialog__empty { display: flex; min-height: 112px; flex-direction: column; align-items: center; justify-content: center; gap: 5px; border: 1px dashed #dfe5df; border-radius: 10px; color: #8b968e; text-align: center; }
.curriculum-dialog__empty strong { color: #526057; font-size: 12px; }
.curriculum-dialog__empty span { font-size: 10px; }
.curriculum-dialog__actions { color: #7b8799; font-size: 10px; }
.curriculum-dialog__actions .v-btn { border-radius: 8px; text-transform: none; letter-spacing: 0; }
@media (max-width: 599px) { .curriculum-dialog__body { min-height: 360px; padding: 16px !important; } .curriculum-dialog__title p { display: none; } }
</style>
